import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { Users, Plus, Trash2, Shield, Building2, Phone, Eye, EyeOff, Key, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'depot', depot_id: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  // Password change state
  const [passwordChangeModal, setPasswordChangeModal] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Salesperson creation state
  const [salespersonDialogOpen, setSalespersonDialogOpen] = useState(false);
  const [newSalesperson, setNewSalesperson] = useState({ mobile: '', password: '', name: '', depot_id: '' });
  
  // Salesperson depot edit state
  const [depotEditModal, setDepotEditModal] = useState({ open: false, user: null });
  const [selectedDepotId, setSelectedDepotId] = useState('');
  const [updatingDepot, setUpdatingDepot] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, depotsRes] = await Promise.all([
        axios.get(`${API}/users/with-passwords`, { headers: getAuthHeaders() }),
        axios.get(`${API}/depots`, { headers: getAuthHeaders() })
      ]);
      setUsers(usersRes.data);
      setDepots(depotsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to regular users endpoint if with-passwords fails
      try {
        const [usersRes, depotsRes] = await Promise.all([
          axios.get(`${API}/users`, { headers: getAuthHeaders() }),
          axios.get(`${API}/depots`, { headers: getAuthHeaders() })
        ]);
        setUsers(usersRes.data);
        setDepots(depotsRes.data);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const openPasswordChangeModal = (user) => {
    setPasswordChangeModal({ open: true, user });
    setNewPassword('');
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/users/${passwordChangeModal.user.id}/password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success(`Password updated for ${passwordChangeModal.user.name}`);
      setPasswordChangeModal({ open: false, user: null });
      setNewPassword('');
      fetchData(); // Refresh to show new password
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newUser.role === 'depot' && !newUser.depot_id) {
      toast.error('Please select a depot for depot users');
      return;
    }

    try {
      await axios.post(`${API}/auth/register`, {
        ...newUser,
        depot_id: newUser.role === 'depot' ? newUser.depot_id : null
      }, { headers: getAuthHeaders() });
      toast.success('User created successfully');
      setNewUser({ email: '', password: '', name: '', role: 'depot', depot_id: '' });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success(`${userName} deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateSalesperson = async () => {
    if (!newSalesperson.mobile || !newSalesperson.password || !newSalesperson.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!/^[0-9]{10}$/.test(newSalesperson.mobile)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      const payload = {
        ...newSalesperson,
        depot_id: newSalesperson.depot_id || null  // null means "All Depots"
      };
      await axios.post(`${API}/salesperson/create`, payload, { headers: getAuthHeaders() });
      toast.success('Salesperson created successfully');
      setNewSalesperson({ mobile: '', password: '', name: '', depot_id: '' });
      setSalespersonDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create salesperson');
    }
  };

  const openDepotEditModal = (user) => {
    setDepotEditModal({ open: true, user });
    setSelectedDepotId(user.depot_id || '');
  };

  const handleUpdateSalespersonDepot = async () => {
    if (!depotEditModal.user) return;
    
    setUpdatingDepot(true);
    try {
      await axios.put(
        `${API}/salesperson/${depotEditModal.user.id}/depot`,
        { depot_id: selectedDepotId || null },
        { headers: getAuthHeaders() }
      );
      toast.success(`Depot updated for ${depotEditModal.user.name}`);
      setDepotEditModal({ open: false, user: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update depot');
    } finally {
      setUpdatingDepot(false);
    }
  };

  const adminUsers = users.filter(u => u.role === 'admin');
  const depotUsers = users.filter(u => u.role === 'depot');
  const factoryUsers = users.filter(u => u.role === 'factory_admin');
  const salespersonUsers = users.filter(u => u.role === 'salesperson');

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage admin and depot user accounts</p>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={salespersonDialogOpen} onOpenChange={setSalespersonDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-salesperson-btn">
                <Phone className="w-4 h-4 mr-2" />
                Add Salesperson
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create Salesperson</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="sp-name">Full Name</Label>
                  <Input
                    id="sp-name"
                    placeholder="Sales Person Name"
                    value={newSalesperson.name}
                    onChange={(e) => setNewSalesperson(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="sp-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sp-mobile">Mobile Number</Label>
                  <Input
                    id="sp-mobile"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={newSalesperson.mobile}
                    onChange={(e) => setNewSalesperson(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    data-testid="sp-mobile-input"
                  />
                  <p className="text-xs text-slate-500">This will be used to login</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sp-password">Password</Label>
                  <Input
                    id="sp-password"
                    type="password"
                    placeholder="Create a password"
                    value={newSalesperson.password}
                    onChange={(e) => setNewSalesperson(prev => ({ ...prev, password: e.target.value }))}
                    data-testid="sp-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sp-depot">Assigned Depot</Label>
                  <Select 
                    value={newSalesperson.depot_id || 'all'} 
                    onValueChange={(value) => setNewSalesperson(prev => ({ ...prev, depot_id: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="sp-depot-select">
                      <SelectValue placeholder="Select depot (or All Depots)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Depots (can send alerts)</SelectItem>
                      {depots.map(depot => (
                        <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    If assigned to a depot, salesperson can only view that depot's stock
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSalespersonDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateSalesperson} data-testid="create-salesperson-btn">
                  Create Salesperson
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="user-name">Full Name</Label>
                  <Input
                    id="user-name"
                    placeholder="John Doe"
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email Address</Label>
                  <Input
                    id="user-email"
                    type="email"
                    placeholder="user@vankon.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password">Password</Label>
                  <Input
                    id="user-password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="depot">Depot User</SelectItem>
                      <SelectItem value="factory_admin">Factory Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              {newUser.role === 'depot' && (
                <div className="space-y-2">
                  <Label>Assign to Depot</Label>
                  <Select value={newUser.depot_id} onValueChange={(v) => setNewUser(prev => ({ ...prev, depot_id: v }))}>
                    <SelectTrigger data-testid="user-depot-select">
                      <SelectValue placeholder="Select a depot" />
                    </SelectTrigger>
                    <SelectContent>
                      {depots.map(depot => (
                        <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateUser} data-testid="create-user-btn">Create User</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Admin Users */}
      <Card data-testid="admin-users-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-500" />
            Administrators
            <Badge variant="secondary" className="ml-2">{adminUsers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">No admin users</p>
            </div>
          ) : (
            <div className="divide-y">
              {adminUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">Password:</span>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                          {showPasswords[user.id] ? (user.password || '********') : '••••••••'}
                        </code>
                        <button 
                          onClick={() => togglePasswordVisibility(user.id)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-700 border-0">Admin</Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-400 hover:text-blue-500"
                      onClick={() => openPasswordChangeModal(user)}
                      data-testid={`change-password-${user.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {user.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this user account.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Depot Users */}
      <Card data-testid="depot-users-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-500" />
            Depot Users
            <Badge variant="secondary" className="ml-2">{depotUsers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : depotUsers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No depot users. Create users for your depots.</p>
            </div>
          ) : (
            <div className="divide-y">
              {depotUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">Password:</span>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                          {showPasswords[user.id] ? (user.password || '********') : '••••••••'}
                        </code>
                        <button 
                          onClick={() => togglePasswordVisibility(user.id)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 border-0">
                      {user.depot_name || 'No Depot'}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-400 hover:text-blue-500"
                      onClick={() => openPasswordChangeModal(user)}
                      data-testid={`change-password-${user.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {user.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this user account.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Factory Admin Users */}
      <Card data-testid="factory-users-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-orange-500" />
            Factory Admins
            <Badge variant="secondary" className="ml-2">{factoryUsers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : factoryUsers.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No factory admin users.</p>
            </div>
          ) : (
            <div className="divide-y">
              {factoryUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">Password:</span>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                          {showPasswords[user.id] ? (user.password || '********') : '••••••••'}
                        </code>
                        <button 
                          onClick={() => togglePasswordVisibility(user.id)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-700 border-0">Factory Admin</Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-400 hover:text-blue-500"
                      onClick={() => openPasswordChangeModal(user)}
                      data-testid={`change-password-${user.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {user.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this user account.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Salesperson Users */}
      <Card data-testid="salesperson-users-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Phone className="w-5 h-5 text-green-500" />
            Salespersons
            <Badge variant="secondary" className="ml-2">{salespersonUsers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : salespersonUsers.length === 0 ? (
            <div className="text-center py-8">
              <Phone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No salespersons. Click "Add Salesperson" to create one.</p>
              <p className="text-xs text-slate-400 mt-1">Salespersons can search & view stock levels on mobile</p>
            </div>
          ) : (
            <div className="divide-y">
              {salespersonUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <p className="text-sm text-slate-500">Mobile: {user.mobile || 'N/A'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">Password:</span>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                          {showPasswords[user.id] ? (user.password || '********') : '••••••••'}
                        </code>
                        <button 
                          onClick={() => togglePasswordVisibility(user.id)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">Depot:</span>
                        <button
                          onClick={() => openDepotEditModal(user)}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {user.depot_name || 'All Depots'} (click to change)
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={user.depot_id ? "bg-blue-100 text-blue-700 border-0" : "bg-green-100 text-green-700 border-0"}>
                      {user.depot_name || 'All Depots'}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-400 hover:text-blue-500"
                      onClick={() => openPasswordChangeModal(user)}
                      data-testid={`change-password-${user.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {user.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this salesperson account.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Change Modal */}
      <Dialog open={passwordChangeModal.open} onOpenChange={(open) => setPasswordChangeModal({ open, user: passwordChangeModal.user })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-500" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          {passwordChangeModal.user && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium text-slate-900">{passwordChangeModal.user.name}</p>
                <p className="text-sm text-slate-500">
                  {passwordChangeModal.user.email || passwordChangeModal.user.mobile}
                </p>
                <Badge className="mt-2" variant="secondary">
                  {passwordChangeModal.user.role}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="text"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                />
                <p className="text-xs text-slate-500">Minimum 4 characters. Password will be visible to admin.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPasswordChangeModal({ open: false, user: null })}
              disabled={changingPassword}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={changingPassword || !newPassword}
              data-testid="confirm-change-password-btn"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salesperson Depot Edit Modal */}
      <Dialog open={depotEditModal.open} onOpenChange={(open) => setDepotEditModal({ open, user: depotEditModal.user })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Change Assigned Depot
            </DialogTitle>
          </DialogHeader>
          {depotEditModal.user && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium text-slate-900">{depotEditModal.user.name}</p>
                <p className="text-sm text-slate-500">Mobile: {depotEditModal.user.mobile}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Current: {depotEditModal.user.depot_name || 'All Depots'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-depot">Assigned Depot</Label>
                <Select value={selectedDepotId || 'all'} onValueChange={(value) => setSelectedDepotId(value === 'all' ? '' : value)}>
                  <SelectTrigger data-testid="edit-depot-select">
                    <SelectValue placeholder="Select depot" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Depots (can send alerts)</SelectItem>
                    {depots.map(depot => (
                      <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {selectedDepotId 
                    ? "Salesperson will only see stock from this depot and cannot send alerts"
                    : "Salesperson can see all depots and send stock alerts"}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDepotEditModal({ open: false, user: null })}
              disabled={updatingDepot}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateSalespersonDepot} 
              disabled={updatingDepot}
              data-testid="confirm-depot-change-btn"
            >
              {updatingDepot ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Depot'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
